/**
 * src/components/spatial/SpatialObjectsContext.tsx
 *
 * Shared React state for the GeoLink spatial-objects subsystem. Sits
 * between the data layer (`geoLinkSpatialObjectsClient`) and the map
 * layers (`SpatialObjectExtrusionLayer`, …) so the user-facing controls
 * (`SpatialObjectsControls`) can drive every layer with a single
 * source of truth — and so the click-interaction panel can read which
 * floor / slice the user is currently focused on without prop-drilling.
 *
 * This context also owns the **MapTiler complement** state: whether
 * the optional MapTiler raster overlay is on, which style it shows,
 * and at what opacity. MapTiler complements Mapbox (it's never the
 * basemap for these flows) so toggling it on/off only adds/removes
 * one raster source + layer; everything else (3D Mapbox buildings,
 * agents, geofences, …) keeps painting unchanged.
 */

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import {
  getMapTilerOverlayOpacity,
  getMapTilerOverlayStyle,
  getMapTilerKey,
  isMapTilerOverlayEnabled,
} from '../../services/spatial/spatialEnv';

/**
 * Which rendering pipeline drives the spatial-objects layer.
 *
 *   • 'legacy' — fetch from GeoLink's `/render` envelope (full geometry +
 *     height + `metadata.tiers`) and paint with the native Mapbox
 *     fill-extrusion layers. This is the only path that renders stepped
 *     buildings (e.g. Burj Khalifa) with correct per-tier base/height and
 *     un-simplified footprints, so it is the DEFAULT.
 *   • 'phase2' — fetch from the normalized `/api/map/*` summary API and
 *     paint through the deck.gl overlay + layer registry. The summary
 *     contract omits height/tier/detailed-geometry for spatial objects,
 *     so 3D buildings are approximate here; use it to exercise the new
 *     aggregate layers and MVT/delta plumbing.
 */
export type SpatialRenderPipeline = 'legacy' | 'phase2';

/** Toggle flags for the show-only-X chips. */
export interface SpatialObjectsFilters {
  /** Show only objects whose `executionRuleId` is non-null. */
  policyLinkedOnly: boolean;
  /** Show only objects whose `contractAddress` is non-null. */
  contractLinkedOnly: boolean;
  /**
   * Show only objects carrying `metadata.event`.
   *
   * "What is on around here" is the question a map of occasions answers,
   * and until now `linkHasEvent` was computed on every feature and read by
   * nothing.
   */
  eventsOnly: boolean;
}

/** Camera presentation modes for the building. */
export interface SpatialObjectsView {
  /** Master visibility toggle for the entire spatial-objects subsystem. */
  enabled: boolean;
  /**
   * Floor index the user is focused on (matches GeoLink's canonical
   * `floorNumber`). When `null`, every floor renders normally.
   */
  selectedFloorNumber: number | null;
  /**
   * Show a thin slab feature at a fixed Y inside the building. Mutually
   * exclusive with a non-null selectedFloorNumber.
   */
  sliceMode: boolean;
  /** Spread the floors apart vertically so each floor's interior is visible. */
  explodeFloors: boolean;
  /** Gap between exploded floors in metres. Defaults to 8. */
  explodeGapMeters: number;
}

/** State for the MapTiler raster tile complement layer. */
export interface MapTilerOverlayState {
  /** Whether MapTiler tiles are enabled (requires a key). */
  enabled: boolean;
  /** MapTiler style id (e.g. "satellite-v2", "topo-v2"). */
  style: string;
  /** Opacity 0..1. */
  opacity: number;
  /** True when REACT_APP_MAPTILER_KEY is configured. Read-only. */
  available: boolean;
}

export interface SpatialObjectsContextValue {
  view: SpatialObjectsView;
  filters: SpatialObjectsFilters;
  mapTiler: MapTilerOverlayState;
  /** Active spatial-objects rendering pipeline (see SpatialRenderPipeline). */
  renderPipeline: SpatialRenderPipeline;

  setEnabled(enabled: boolean): void;
  setSelectedFloorNumber(floor: number | null): void;
  setSliceMode(enabled: boolean): void;
  setExplodeFloors(enabled: boolean): void;
  setExplodeGap(metres: number): void;

  setPolicyLinkedOnly(enabled: boolean): void;
  setContractLinkedOnly(enabled: boolean): void;
  setEventsOnly(enabled: boolean): void;
  setFilters(updater: (f: SpatialObjectsFilters) => SpatialObjectsFilters): void;

  setMapTilerOverlayEnabled(enabled: boolean): void;
  setMapTilerOverlayStyle(style: string): void;
  setMapTilerOverlayOpacity(opacity: number): void;

  setRenderPipeline(pipeline: SpatialRenderPipeline): void;
}

const defaultView: SpatialObjectsView = {
  enabled:             true,
  selectedFloorNumber: null,
  sliceMode:           false,
  explodeFloors:       false,
  explodeGapMeters:    8,
};

const defaultFilters: SpatialObjectsFilters = {
  policyLinkedOnly:   false,
  contractLinkedOnly: false,
  eventsOnly:         false,
};

function buildDefaultMapTilerState(): MapTilerOverlayState {
  const available = !!getMapTilerKey();
  return {
    enabled:   available && isMapTilerOverlayEnabled(),
    style:     getMapTilerOverlayStyle(),
    opacity:   getMapTilerOverlayOpacity(),
    available,
  };
}

const SpatialObjectsContext = createContext<SpatialObjectsContextValue | null>(null);

export interface SpatialObjectsProviderProps {
  initialView?: Partial<SpatialObjectsView>;
  initialFilters?: Partial<SpatialObjectsFilters>;
  initialMapTilerOverlay?: Partial<MapTilerOverlayState>;
  /**
   * Initial rendering pipeline. Defaults to 'legacy' so 3D buildings are
   * accurate out of the box regardless of the deck/map-API env flags.
   */
  initialRenderPipeline?: SpatialRenderPipeline;
  children: React.ReactNode;
}

export const SpatialObjectsProvider: React.FC<SpatialObjectsProviderProps> = ({
  initialView,
  initialFilters,
  initialMapTilerOverlay,
  initialRenderPipeline,
  children,
}) => {
  const [view, setView] = useState<SpatialObjectsView>({ ...defaultView, ...initialView });
  const [filters, setFiltersState] = useState<SpatialObjectsFilters>({ ...defaultFilters, ...initialFilters });
  const [mapTiler, setMapTilerState] = useState<MapTilerOverlayState>({
    ...buildDefaultMapTilerState(),
    ...initialMapTilerOverlay,
  });
  const [renderPipeline, setRenderPipelineState] = useState<SpatialRenderPipeline>(
    initialRenderPipeline ?? 'legacy',
  );

  const setEnabled            = useCallback((enabled: boolean)        => setView(v => ({ ...v, enabled })), []);
  const setSelectedFloorNumber = useCallback((selectedFloorNumber: number | null) => {
    // Slice mode is anchored on the whole building — selecting a floor
    // implicitly cancels slice mode so the two modes don't compete.
    setView(v => ({ ...v, selectedFloorNumber, sliceMode: selectedFloorNumber !== null ? false : v.sliceMode }));
  }, []);
  const setSliceMode      = useCallback((sliceMode: boolean)    => {
    setView(v => ({ ...v, sliceMode, selectedFloorNumber: sliceMode ? null : v.selectedFloorNumber }));
  }, []);
  const setExplodeFloors  = useCallback((explodeFloors: boolean)=> setView(v => ({ ...v, explodeFloors })), []);
  const setExplodeGap     = useCallback((explodeGapMeters: number) => setView(v => ({ ...v, explodeGapMeters })), []);

  const setPolicyLinkedOnly   = useCallback((policyLinkedOnly: boolean) => setFiltersState(f => ({ ...f, policyLinkedOnly })), []);
  const setContractLinkedOnly = useCallback((contractLinkedOnly: boolean) => setFiltersState(f => ({ ...f, contractLinkedOnly })), []);
  const setEventsOnly         = useCallback((eventsOnly: boolean) => setFiltersState(f => ({ ...f, eventsOnly })), []);
  const setFilters            = useCallback((updater: (f: SpatialObjectsFilters) => SpatialObjectsFilters) => setFiltersState(updater), []);

  const setMapTilerOverlayEnabled = useCallback((enabled: boolean) => {
    // Disable silently when no key is configured — a clearer error
    // is surfaced by the controls component, which dims the toggle.
    setMapTilerState(m => ({ ...m, enabled: m.available ? enabled : false }));
  }, []);
  const setMapTilerOverlayStyle = useCallback((style: string) => {
    setMapTilerState(m => ({ ...m, style }));
  }, []);
  const setMapTilerOverlayOpacity = useCallback((opacity: number) => {
    setMapTilerState(m => ({ ...m, opacity: Math.max(0, Math.min(1, opacity)) }));
  }, []);

  const setRenderPipeline = useCallback((pipeline: SpatialRenderPipeline) => {
    setRenderPipelineState(pipeline);
  }, []);

  const value = useMemo<SpatialObjectsContextValue>(() => ({
    view, filters, mapTiler, renderPipeline,
    setEnabled, setSelectedFloorNumber, setSliceMode, setExplodeFloors, setExplodeGap,
    setPolicyLinkedOnly, setContractLinkedOnly, setEventsOnly, setFilters,
    setMapTilerOverlayEnabled, setMapTilerOverlayStyle, setMapTilerOverlayOpacity,
    setRenderPipeline,
  }), [
    view, filters, mapTiler, renderPipeline,
    setEnabled, setSelectedFloorNumber, setSliceMode, setExplodeFloors, setExplodeGap,
    setPolicyLinkedOnly, setContractLinkedOnly, setEventsOnly, setFilters,
    setMapTilerOverlayEnabled, setMapTilerOverlayStyle, setMapTilerOverlayOpacity,
    setRenderPipeline,
  ]);

  return (
    <SpatialObjectsContext.Provider value={value}>
      {children}
    </SpatialObjectsContext.Provider>
  );
};

export function useSpatialObjects(): SpatialObjectsContextValue {
  const ctx = useContext(SpatialObjectsContext);
  if (!ctx) {
    throw new Error('useSpatialObjects must be used within <SpatialObjectsProvider>');
  }
  return ctx;
}

/** Optional hook variant — returns `null` when not inside a provider. */
export function useSpatialObjectsOptional(): SpatialObjectsContextValue | null {
  return useContext(SpatialObjectsContext);
}
